import type { PaymentEventDef, PaymentEventTier } from '@rb/shared/master-data/types';
import type { EnvSchema } from '@/master-data/EnvSelector';
import { callEdgeFunction } from '@/master-data/supabase.client';
import type { PaymentEventInsertValues, PaymentEventRowValues } from './payment-event.model';

const TABLE = 'payment_event';

interface ListResp {
  rows: Record<string, unknown>[];
}

/** BIGINT 는 EF 를 거치며 문자열로 올 수 있어 명시적으로 좁힌다. */
function rowToDef(row: Record<string, unknown>): PaymentEventDef {
  return {
    id: Number(row.id),
    active: Boolean(row.active),
    start_at: Number(row.start_at),
    end_at: Number(row.end_at),
    grace_days: Number(row.grace_days),
    title_key: (row.title_key as string | null) ?? undefined,
    tiers: (row.tiers as PaymentEventTier[] | null) ?? [],
  };
}

export async function listPaymentEvents(env: EnvSchema): Promise<PaymentEventDef[]> {
  const res = await callEdgeFunction<ListResp>('crud', {
    schema: env,
    table: TABLE,
    op: 'list',
    limit: 1000,
  });
  return (res.rows ?? []).map(rowToDef).sort((a, b) => a.id - b.id);
}

export async function insertPaymentEvent(
  env: EnvSchema,
  values: PaymentEventInsertValues
): Promise<void> {
  await callEdgeFunction('crud', { schema: env, table: TABLE, op: 'insert', values });
}

/**
 * 편집 폼 저장 경로. `PaymentEventRowValues` 에 `active` 가 없으므로 이 경로로는
 * 활성 플래그를 건드릴 수 없다 — 폼이 들고 있던 낡은 active 스냅샷이 라디오로 이미
 * 바뀐 DB 상태를 되돌려 "active 2개" 를 만드는 사고를 타입 레벨에서 차단한다.
 */
export async function updatePaymentEvent(
  env: EnvSchema,
  id: number,
  values: PaymentEventRowValues
): Promise<void> {
  await callEdgeFunction('crud', {
    schema: env,
    table: TABLE,
    op: 'update',
    pk: { id },
    values,
  });
}

/** 활성 플래그 전용 update — 다른 컬럼과 절대 섞지 않는다. */
async function setActiveFlag(env: EnvSchema, id: number, active: boolean): Promise<void> {
  await callEdgeFunction('crud', {
    schema: env,
    table: TABLE,
    op: 'update',
    pk: { id },
    values: { active },
  });
}

export async function deletePaymentEvent(env: EnvSchema, id: number): Promise<void> {
  await callEdgeFunction('crud', { schema: env, table: TABLE, op: 'delete', pk: { id } });
}

/**
 * 단일 active 불변식을 유지하며 활성 이벤트를 전환한다.
 *
 * crud EF 에는 트랜잭션이 없으므로 순차 호출한다. 기존 active 를 먼저 내리는
 * 순서라 중간에 실패해도 active 가 2개가 되지는 않는다(0개는 가능). 이 중간
 * 상태는 게임에 영향이 없다 — 실제 반영은 Apply 시점의 R2 빌드다.
 */
export async function setActivePaymentEvent(
  env: EnvSchema,
  id: number,
  rows: PaymentEventDef[]
): Promise<void> {
  for (const r of rows) {
    if (r.active && r.id !== id) await setActiveFlag(env, r.id, false);
  }
  await setActiveFlag(env, id, true);
}
